import { requestTabCaptureStream } from '@sorrem-client/ui/telemetry';

/** How long to wait for a fresh frame: a still page sends none, so then the last one is used. */
const FRESH_FRAME_WAIT_MS = 250;

interface TabCapture {
    readonly stream: MediaStream;
    readonly video: HTMLVideoElement;
}

/** The open tab capture, kept between snapshots so only the first one asks. */
let open: TabCapture | null = null;

/** Stops sharing the tab: Chrome's sharing bar goes away, and the next snapshot asks again. */
export function closeTabSnapshotStream(): void {
    open?.stream.getTracks().forEach((track) => track.stop());
    if (open) {
        open.video.srcObject = null;
    }
    open = null;
}

async function openTabCapture(): Promise<TabCapture | null> {
    if (open?.stream.getVideoTracks().some((track) => track.readyState === 'live')) {
        return open;
    }
    closeTabSnapshotStream();
    const stream = await requestTabCaptureStream();
    if (!stream) {
        return null;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    // Stop sharing in Chrome's bar ends the track; forget it so the next snapshot asks again.
    stream.getVideoTracks()[0]?.addEventListener('ended', closeTabSnapshotStream);
    open = { stream, video };
    return open;
}

function waitForFrame(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve) => {
        const timeoutId = setTimeout(resolve, FRESH_FRAME_WAIT_MS);
        video.requestVideoFrameCallback(() => {
            clearTimeout(timeoutId);
            resolve();
        });
    });
}

/**
 * The tab as it looks right now, HUD and all, as a full-size PNG: a
 * developer snapshot (#303). The first call asks Chrome to share the tab
 * (call it from a click or key press); the stream then stays open, under
 * Chrome's sharing bar, so later calls are instant. Resolves to null when
 * the developer declines.
 */
export async function captureTabSnapshot(): Promise<Blob | null> {
    const capture = await openTabCapture();
    if (!capture) {
        return null;
    }
    const { video } = capture;
    await waitForFrame(video);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
        throw new Error('The snapshot could not be encoded.');
    }
    return blob;
}
